import { supabase } from './supabase';

const USE_MOCK_DATA = true;

interface OutlookConnection {
  id?: string;
  user_id: string;
  client_id?: string;
  email: string;
  access_token: string;
  refresh_token: string;
  token_expires_at: string;
  calendar_id?: string;
  is_active?: boolean;
  sync_enabled?: boolean;
  sync_direction?: 'to_outlook' | 'from_outlook' | 'both';
}

interface ProjectCollaborator {
  id?: string;
  client_id: string;
  user_id?: string;
  name: string;
  email: string;
  role: 'account_manager' | 'content_creator' | 'designer' | 'copywriter' | 'quality_reviewer';
  outlook_email?: string;
  is_active?: boolean;
  receive_calendar_invites?: boolean;
  notification_preferences?: any;
}

interface OutlookEvent {
  id?: string;
  connection_id: string;
  post_id?: string;
  client_id: string;
  outlook_event_id?: string;
  subject: string;
  body?: string;
  start_time: string;
  end_time: string;
  location?: string;
  attendees?: any[];
  is_all_day?: boolean;
  reminder_minutes?: number;
  status?: 'free' | 'tentative' | 'busy' | 'oof' | 'workingElsewhere';
  is_meeting?: boolean;
  meeting_url?: string;
}

class OutlookCalendarService {
  /**
   * OAuth and Connection Management
   */
  async initiateOAuthFlow(redirectUri: string) {
    const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
    const scopes = [
      'Calendars.ReadWrite',
      'Calendars.ReadWrite.Shared',
      'User.Read',
      'offline_access'
    ].join(' ');

    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
      `client_id=${clientId}` +
      `&response_type=code` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(scopes)}` +
      `&response_mode=query`;

    return authUrl;
  }

  async exchangeCodeForToken(code: string, redirectUri: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: {
          access_token: 'mock_access_token_' + Date.now(),
          refresh_token: 'mock_refresh_token_' + Date.now(),
          expires_in: 3600,
          email: 'user@example.com'
        }
      };
    }

    try {
      const response = await fetch('https://login.microsoftonline.com/common/oauth2/v2.0/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          client_id: import.meta.env.VITE_MICROSOFT_CLIENT_ID,
          client_secret: import.meta.env.VITE_MICROSOFT_CLIENT_SECRET,
          code: code,
          redirect_uri: redirectUri,
          grant_type: 'authorization_code'
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error_description || 'Token exchange failed');

      return { success: true, data };
    } catch (error: any) {
      console.error('[Outlook] Token exchange error:', error);
      return { success: false, error: error.message };
    }
  }

  async refreshAccessToken(refreshToken: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: {
          access_token: 'mock_refreshed_token_' + Date.now(),
          expires_in: 3600
        }
      };
    }

    try {
      const response = await fetch('https://login.microsoftonline.com/common/oauth2/v2.0/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          client_id: import.meta.env.VITE_MICROSOFT_CLIENT_ID,
          client_secret: import.meta.env.VITE_MICROSOFT_CLIENT_SECRET,
          refresh_token: refreshToken,
          grant_type: 'refresh_token'
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error_description || 'Token refresh failed');

      return { success: true, data };
    } catch (error: any) {
      console.error('[Outlook] Token refresh error:', error);
      return { success: false, error: error.message };
    }
  }

  async createConnection(connection: OutlookConnection) {
    if (USE_MOCK_DATA) {
      console.log('[Outlook] Mock: Creating connection', connection);
      return {
        success: true,
        data: { id: `conn_${Date.now()}`, ...connection, is_active: true }
      };
    }

    try {
      const { data, error } = await supabase
        .from('outlook_connections')
        .insert([connection])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      console.error('[Outlook] Create connection error:', error);
      return { success: false, error: error.message };
    }
  }

  async getConnections(userId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'conn_1',
            user_id: userId,
            email: 'user@company.com',
            is_active: true,
            sync_enabled: true,
            last_sync_at: new Date().toISOString()
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('outlook_connections')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[Outlook] Get connections error:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  async disconnectOutlook(connectionId: string) {
    if (USE_MOCK_DATA) {
      console.log('[Outlook] Mock: Disconnecting', connectionId);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('outlook_connections')
        .update({ is_active: false, sync_enabled: false })
        .eq('id', connectionId);

      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('[Outlook] Disconnect error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Project Collaborators Management
   */
  async getCollaborators(clientId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'collab_1',
            client_id: clientId,
            name: 'John Smith',
            email: 'john@company.com',
            role: 'account_manager',
            outlook_email: 'john@company.com',
            is_active: true,
            receive_calendar_invites: true
          },
          {
            id: 'collab_2',
            client_id: clientId,
            name: 'Sarah Johnson',
            email: 'sarah@company.com',
            role: 'content_creator',
            outlook_email: 'sarah@company.com',
            is_active: true,
            receive_calendar_invites: true
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('project_collaborators')
        .select('*')
        .eq('client_id', clientId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[Outlook] Get collaborators error:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  async addCollaborator(collaborator: ProjectCollaborator) {
    if (USE_MOCK_DATA) {
      console.log('[Outlook] Mock: Adding collaborator', collaborator);
      return {
        success: true,
        data: { id: `collab_${Date.now()}`, ...collaborator, is_active: true }
      };
    }

    try {
      const { data, error } = await supabase
        .from('project_collaborators')
        .insert([collaborator])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      console.error('[Outlook] Add collaborator error:', error);
      return { success: false, error: error.message };
    }
  }

  async updateCollaborator(collaboratorId: string, updates: Partial<ProjectCollaborator>) {
    if (USE_MOCK_DATA) {
      console.log('[Outlook] Mock: Updating collaborator', collaboratorId, updates);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('project_collaborators')
        .update(updates)
        .eq('id', collaboratorId);

      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('[Outlook] Update collaborator error:', error);
      return { success: false, error: error.message };
    }
  }

  async removeCollaborator(collaboratorId: string) {
    if (USE_MOCK_DATA) {
      console.log('[Outlook] Mock: Removing collaborator', collaboratorId);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('project_collaborators')
        .update({ is_active: false })
        .eq('id', collaboratorId);

      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('[Outlook] Remove collaborator error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Calendar Event Sync
   */
  async syncPostToOutlook(postId: string, connectionId: string) {
    if (USE_MOCK_DATA) {
      console.log('[Outlook] Mock: Syncing post to Outlook', postId, connectionId);
      return {
        success: true,
        data: {
          event_id: `event_${Date.now()}`,
          sync_status: 'synced'
        }
      };
    }

    try {
      const { data, error } = await supabase
        .rpc('sync_post_to_outlook', {
          p_post_id: postId,
          p_connection_id: connectionId
        });

      if (error) throw error;
      return { success: true, data: data?.[0] };
    } catch (error: any) {
      console.error('[Outlook] Sync post error:', error);
      return { success: false, error: error.message };
    }
  }

  async bulkSyncToOutlook(
    clientId: string,
    connectionId: string,
    dateFrom?: string,
    dateTo?: string
  ) {
    if (USE_MOCK_DATA) {
      console.log('[Outlook] Mock: Bulk syncing to Outlook', clientId, connectionId);
      return {
        success: true,
        data: {
          total_posts: 15,
          synced_count: 14,
          failed_count: 1,
          sync_percentage: 93.3
        }
      };
    }

    try {
      const { data, error } = await supabase
        .rpc('bulk_sync_to_outlook', {
          p_client_id: clientId,
          p_connection_id: connectionId,
          p_date_from: dateFrom || new Date().toISOString(),
          p_date_to: dateTo || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        });

      if (error) throw error;
      return { success: true, data: data?.[0] };
    } catch (error: any) {
      console.error('[Outlook] Bulk sync error:', error);
      return { success: false, error: error.message };
    }
  }

  async getSyncStatus(clientId: string, dateFrom?: string, dateTo?: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: {
          total_posts: 20,
          synced_posts: 17,
          pending_posts: 2,
          failed_posts: 1,
          sync_percentage: 85.0
        }
      };
    }

    try {
      const { data, error } = await supabase
        .rpc('get_sync_status', {
          p_client_id: clientId,
          p_date_from: dateFrom || new Date().toISOString().split('T')[0],
          p_date_to: dateTo || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        });

      if (error) throw error;
      return { success: true, data: data?.[0] };
    } catch (error: any) {
      console.error('[Outlook] Get sync status error:', error);
      return { success: false, error: error.message };
    }
  }

  async getSyncedEvents(clientId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'event_1',
            subject: 'Social Media Post: Instagram - Product Launch',
            start_time: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
            end_time: new Date(Date.now() + 2.5 * 60 * 60 * 1000).toISOString(),
            attendees: ['john@company.com', 'sarah@company.com'],
            sync_status: 'synced'
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('outlook_calendar_events')
        .select(`
          *,
          outlook_calendar_sync (
            sync_status,
            last_synced_at
          )
        `)
        .eq('client_id', clientId)
        .order('start_time', { ascending: true });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[Outlook] Get synced events error:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Microsoft Graph API - Outlook Calendar Operations
   */
  async createOutlookEvent(accessToken: string, event: any) {
    if (USE_MOCK_DATA) {
      console.log('[Outlook] Mock: Creating Outlook event', event);
      return {
        success: true,
        data: { id: `outlook_event_${Date.now()}`, ...event }
      };
    }

    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me/events', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(event)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error?.message || 'Failed to create event');

      return { success: true, data };
    } catch (error: any) {
      console.error('[Outlook] Create event error:', error);
      return { success: false, error: error.message };
    }
  }

  async updateOutlookEvent(accessToken: string, eventId: string, updates: any) {
    if (USE_MOCK_DATA) {
      console.log('[Outlook] Mock: Updating Outlook event', eventId, updates);
      return { success: true };
    }

    try {
      const response = await fetch(`https://graph.microsoft.com/v1.0/me/events/${eventId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to update event');
      }

      return { success: true };
    } catch (error: any) {
      console.error('[Outlook] Update event error:', error);
      return { success: false, error: error.message };
    }
  }

  async deleteOutlookEvent(accessToken: string, eventId: string) {
    if (USE_MOCK_DATA) {
      console.log('[Outlook] Mock: Deleting Outlook event', eventId);
      return { success: true };
    }

    try {
      const response = await fetch(`https://graph.microsoft.com/v1.0/me/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok && response.status !== 204) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to delete event');
      }

      return { success: true };
    } catch (error: any) {
      console.error('[Outlook] Delete event error:', error);
      return { success: false, error: error.message };
    }
  }

  async getOutlookEvents(accessToken: string, startDate?: string, endDate?: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: []
      };
    }

    try {
      let url = 'https://graph.microsoft.com/v1.0/me/calendarview?';
      if (startDate) url += `startDateTime=${startDate}&`;
      if (endDate) url += `endDateTime=${endDate}&`;
      url += '$select=subject,start,end,location,attendees,body&$orderby=start/dateTime';

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error?.message || 'Failed to get events');

      return { success: true, data: data.value || [] };
    } catch (error: any) {
      console.error('[Outlook] Get events error:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Sync Logs
   */
  async getSyncLogs(connectionId?: string, limit = 50) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'log_1',
            operation: 'bulk_sync',
            direction: 'to_outlook',
            status: 'success',
            items_processed: 15,
            items_succeeded: 14,
            items_failed: 1,
            created_at: new Date().toISOString()
          }
        ]
      };
    }

    try {
      let query = supabase
        .from('outlook_sync_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (connectionId) {
        query = query.eq('connection_id', connectionId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[Outlook] Get sync logs error:', error);
      return { success: false, error: error.message, data: [] };
    }
  }
}

export const outlookCalendarService = new OutlookCalendarService();
